/**
 * Shared demo framework — in-memory fixture store factory.
 *
 * Each demo (AR, AP, …) instantiates one of these to hold its whole fixture
 * dataset. A tiny external store (no extra deps — built on `useSyncExternalStore`)
 * living at module scope so hook shims (plain functions, not components nested
 * under a provider) can read the current snapshot synchronously, while React
 * components subscribe for reactive updates.
 *
 *   const store = createDemoStore(buildMyDemoSeed);
 *   store.getState()              — synchronous read (hook shims).
 *   store.useStore(selector)      — reactive read in a component.
 *   store.update(producer)        — mutate (a beat applying a change).
 *   store.reset()                 — one-click reset back to the seed.
 *   <store.StoreProvider>         — optional; re-seeds on mount + deletion boundary.
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';

export interface DemoStore<TState> {
  getState: () => TState;
  useStore: <T>(selector: (s: TState) => T) => T;
  update: (producer: (draft: TState) => TState | void) => void;
  reset: () => void;
  StoreProvider: (props: PropsWithChildren) => ReactNode;
}

/**
 * Structural equality for demo snapshots. Fixture slices are plain JSON-like
 * data (objects, arrays, primitives), so a recursive compare is enough.
 */
function snapshotEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== 'object' ||
    a === null ||
    typeof b !== 'object' ||
    b === null
  ) {
    return false;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((item, i) => snapshotEqual(item, b[i]));
  }
  const aRec = a as Record<string, unknown>;
  const bRec = b as Record<string, unknown>;
  const aKeys = Object.keys(aRec);
  if (aKeys.length !== Object.keys(bRec).length) return false;
  return aKeys.every(
    (k) =>
      Object.prototype.hasOwnProperty.call(bRec, k) &&
      snapshotEqual(aRec[k], bRec[k]),
  );
}

export function createDemoStore<TState>(
  buildSeed: () => TState,
): DemoStore<TState> {
  let state: TState = buildSeed();
  const listeners = new Set<() => void>();

  function emit(): void {
    for (const listener of listeners) listener();
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function getState(): TState {
    return state;
  }

  /**
   * Apply a mutation. The producer receives a fresh deep clone it may mutate in
   * place and/or return; whatever it returns (or the mutated clone) becomes the
   * new snapshot. Always produces a new top-level reference so subscribers
   * re-render.
   */
  function update(producer: (draft: TState) => TState | void): void {
    const draft = structuredClone(state);
    const next = producer(draft);
    state = next ?? draft;
    emit();
  }

  function reset(): void {
    state = buildSeed();
    emit();
  }

  /**
   * Reactive selector hook. Re-renders the caller when the selected slice
   * changes (by structural equality). We recompute the selector each render (so
   * selectors closing over args like an `id` stay correct) but return the
   * previously-cached reference whenever the result is structurally unchanged,
   * keeping the `useSyncExternalStore` snapshot stable (no infinite loop).
   */
  function useStore<T>(selector: (s: TState) => T): T {
    const cache = useRef<{ value: T } | null>(null);
    const getSnapshot = (): T => {
      const next = selector(state);
      if (cache.current && snapshotEqual(cache.current.value, next)) {
        return cache.current.value;
      }
      cache.current = { value: next };
      return next;
    };
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  }

  /**
   * Optional provider — re-seeds the store on mount so each fresh entry into the
   * demo (e.g. a reload onto a `?demo=…` page) starts clean. Mounted only inside
   * the demo wrapper so it never affects production.
   */
  function StoreProvider({ children }: PropsWithChildren): ReactNode {
    useEffect(() => {
      reset();
    }, []);
    return children;
  }

  return { getState, useStore, update, reset, StoreProvider };
}
